/* eslint-disable no-unused-vars */
import React, { lazy, useEffect } from "react";
import { Route, Switch, Redirect, useLocation, useHistory } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import PolicyPage from "pages/Policy";
import TermPage from "pages/Term";
import SettingLevel from "pages/SettingLevel";

import ReactGA from "react-ga4";
import { useDispatch } from "redux/store";
import { setPermission } from "redux/slices/role";
import { useQuery, useReactiveVar } from "@apollo/client";
import { GetUserRoleDocument } from "__generated__/graphql";
import { isAuth as isAuthVar } from "apollo/cache";
import { FLAG_WHAT_IS_NEW } from "state/useFlag";

const RiceInspectOptionPage = lazy(() => import("pages/InspectOption"));
const WhitenessInspectOptionPage = lazy(() => import("pages/WhitenessOption"));
const RiceInspectUpload = lazy(() => import("pages/RiceInspectUpload"));
const DashboardPage = lazy(() => import("pages/Dashboard"));
const ProfilePage = lazy(() => import("pages/Profile"));
const RiceInspectHistoryPage = lazy(() => import("pages/RiceInspectHistory"));
const WhitenessHistoryPage = lazy(() => import("pages/WhitenessHistory"));
const MainMenuPage = lazy(() => import("pages/MainMenu"));
const SettingMenuPage = lazy(() => import("pages/RiceSetting/RiceSettingMenu"));
const SettingStandardPage = lazy(() => import("pages/SettingStandard"));
const SettingFormPage = lazy(() => import("pages/SettingForm"));
const RiceStandardDetailPage = lazy(() => import("pages/RiceSetting/standard/RiceStandardDetail"));
const RiceStandardDetailSharePage = lazy(() =>
  import("pages/RiceSetting/standard/RiceStandardDetailShare")
);
const RiceFormDetailPage = lazy(() => import("pages/RiceSetting/forms/RiceFormDetail"));
const StandardEditPage = lazy(() => import("pages/StandardEdit"));

const RiceSettingPresetListPage = lazy(() =>
  import("pages/RiceSetting/preset/RiceSettingPresetList")
);
const RiceSettingPresetCreatePage = lazy(() =>
  import("pages/RiceSetting/preset/RiceSettingPresetCreate")
);
const RiceSettingPresetEditPage = lazy(() =>
  import("pages/RiceSetting/preset/RiceSettingPresetEdit")
);

const RiceSettingInspectLevelPage = lazy(() => import("pages/RiceSetting/RiceSettingInspectLevel"));

const NotFoundPage = lazy(() => import("pages/NotFound"));
const NotFoundScannerPage = lazy(() => import("pages/NotFoundScanner"));

const LoginPage = lazy(() => import("pages/Login"));
const ForgotPage = lazy(() => import("pages/Forgot"));

const TutorialBeta = lazy(() => import("pages/TutorialBeta"));
const AboutTest = lazy(() => import("pages/About"));
const RiceInspectResultNew = lazy(() => import("pages/RiceInspectResult/new"));
const WhitenessResultNew = lazy(() => import("pages/WhitenessResult/new"));
const UserManagementPage = lazy(() => import("pages/UserManagement"));
const AccessDeniedPage = lazy(() => import("pages/AccessDenied"));

const usePageTracking = () => {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);
};

const Router = () => {
  usePageTracking();

  const dispatch = useDispatch();
  const { listen } = useHistory();
  const { refetch } = useQuery(GetUserRoleDocument, {
    skip: true,
    onCompleted: (data) => {
      dispatch(setPermission(data?.getUser?.role));
    },
  });
  const isAuth = useReactiveVar(isAuthVar);

  useEffect(() => {
    const unlisten = listen((data) => {
      if (isAuth) {
        refetch();
      }
    });
    return unlisten;
  }, [listen]);
  return (
    <Switch>
      <Route
        exact
        path="/"
        render={() => {
          // return <Redirect to="/mainmenu" />;
          return <Redirect to="/rice-inspect-option" />;
        }}
      />
      <Route exact path="/term" component={TermPage} />
      <Route exact path="/policy" component={PolicyPage} />
      <PublicRoute restricted={true} exact path="/login" component={LoginPage} />
      <PublicRoute restricted={true} exact path="/forgot" component={ForgotPage} />
      <Route exact path="/about" component={AboutTest} />
      <PrivateRoute exact path="/tutorial-beta" component={TutorialBeta} />
      <PrivateRoute exact path="/mainmenu" component={MainMenuPage} />
      <PrivateRoute
        exact
        path="/rice-inspect-option"
        component={RiceInspectOptionPage}
        feature="rice_inspection:create"
        // flagWhatNew={FLAG_WHAT_IS_NEW.inspect}
        flagWhatNew={FLAG_WHAT_IS_NEW.whiteness_inspect}
      />
      <PrivateRoute
        exact
        path="/whiteness-inspect-option"
        // feature="whiteness_inspection:create"
        // flagWhatNew={FLAG_WHAT_IS_NEW.whiteness}
        component={WhitenessInspectOptionPage}
      />
      <PrivateRoute
        exact
        path="/rice-inspect-upload"
        component={RiceInspectUpload}
        feature="rice_inspection:create"
      />
      <PublicRoute
        restricted={false}
        exact
        path="/rice-inspect-result"
        component={RiceInspectResultNew}
        // flagWhatNew={FLAG_WHAT_IS_NEW.result}
        flagWhatNew={FLAG_WHAT_IS_NEW.whiteness_result}
      />
      <PublicRoute
        restricted={false}
        exact
        path="/whiteness-result"
        component={WhitenessResultNew}
        // flagWhatNew={FLAG_WHAT_IS_NEW.result}
      />
      <PrivateRoute
        exact
        path="/history-rice-inspect"
        component={RiceInspectHistoryPage}
        feature="rice_inspection:view"
        flagWhatNew={FLAG_WHAT_IS_NEW.whiteness_history}
      />
      <PrivateRoute
        exact
        path="/setting"
        component={SettingMenuPage}
        feature="rice_inspection.setting:view"
        flagWhatNew={FLAG_WHAT_IS_NEW.setting}
      />
      <PrivateRoute exact path="/history-whiteness" component={WhitenessHistoryPage} />
      <PrivateRoute exact path="/setting/inspect-level" component={RiceSettingInspectLevelPage} />
      {/* <PrivateRoute
        exact
        path="/setting/old/inspect-level"
        component={SettingLevel}
        provider={SettingMenuProvider}
      /> */}
      <PrivateRoute exact path="/setting/preset" component={RiceSettingPresetListPage} />
      <PrivateRoute exact path="/setting/preset/create" component={RiceSettingPresetCreatePage} />
      <PrivateRoute exact path="/setting/preset/:id" component={RiceSettingPresetEditPage} />
      <PrivateRoute exact path="/standards" component={SettingStandardPage} />
      <PrivateRoute exact path="/forms" component={SettingFormPage} />
      <PrivateRoute exact path="/forms/:id" component={RiceFormDetailPage} />
      <PrivateRoute exact path="/standards/view" component={RiceStandardDetailPage} />
      <PublicRoute
        restricted={false}
        exact
        path="/standards/view/share"
        component={RiceStandardDetailSharePage}
      />
      <PrivateRoute
        exact
        path="/standards/edit"
        component={StandardEditPage}
        feature="rice_inspection.setting:standard.edit"
      />
      <PrivateRoute exact path="/dashboard" component={DashboardPage} feature="dashboard:view" />
      <PrivateRoute exact path="/404" component={NotFoundPage} />
      <PrivateRoute exact path="/403" component={AccessDeniedPage} />
      <PrivateRoute exact path="/not-found-scanner" component={NotFoundScannerPage} />
      <Redirect exact from={`/profile`} to={`/profile/info`} />
      <PrivateRoute
        path="/profile"
        component={ProfilePage}
        flagWhatNew={FLAG_WHAT_IS_NEW.profile}
      />
      <PrivateRoute
        path="/user-management"
        component={UserManagementPage}
        feature="user_management:view"
      />
      <PrivateRoute component={NotFoundPage} />
    </Switch>
  );
};

export default Router;

export function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
